import { Card, CardContent, Typography, Box } from "@mui/material";

const COLORS = [
  "#2a9d8f", // At Entry - teal
  "#f4a261", // Post De-stuffing - amber
  "#e76f51", // After Repair - coral
  "#4361ee", // During Stuffing - indigo
  "#06d6a0", // Post Stuffing - mint
  "#ef476f", // At Exit - rose
];

const DonutChart = ({ passed, total, color }) => {
  const size = 100;
  const strokeWidth = 9;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = total > 0 ? passed / total : 0;
  const filledDash = pct * circumference;
  const center = size / 2;
  const displayPct = total > 0 ? Math.round(pct * 100) : 0;

  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#eef1f7"
          strokeWidth={strokeWidth}
        />
        {/* Colored arc */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={`${filledDash} ${circumference}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
          style={{ transition: "stroke-dasharray 0.9s ease" }}
        />
      </svg>

      {/* Center label */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 800,
            color: color,
            lineHeight: 1,
            fontFamily: "'Outfit', sans-serif",
          }}
        >
          {displayPct}%
        </Typography>
        <Typography
          sx={{
            fontSize: "8px",
            fontWeight: 700,
            color: "#b0bec5",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            mt: "3px",
          }}
        >
          PASS
        </Typography>
      </Box>
    </Box>
  );
};

const TwoSectionCard = ({ leftTitle, leftValue, rightTitle, rightValue, colorIndex = 0 }) => {
  const passed = parseInt(rightValue) || 0;
  const total = parseInt(leftValue) || 0;
  const failed = total - passed;
  const color = COLORS[colorIndex % COLORS.length];
  const isPassed = passed > 0;

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: "14px",
        border: "1.5px solid #e8eef6",
        boxShadow: "0 2px 10px rgba(30,50,90,0.06)",
        transition: "transform 0.18s, box-shadow 0.18s",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 10px 28px rgba(30,50,90,0.11)",
        },
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        {/* Stage label at top */}
        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid #f0f4fa" }}>
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: "#9ab0c8",
              letterSpacing: "0.9px",
              textTransform: "uppercase",
            }}
          >
            {leftTitle}
          </Typography>
        </Box>

        {/* Main body: donut LEFT, stats RIGHT */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2.5,
            py: 2,
            gap: 2.5,
          }}
        >
          {/* LEFT: Donut */}
          <DonutChart passed={passed} total={total} color={color} />

          {/* RIGHT: Number + passed/failed */}
          <Box sx={{ flex: 1 }}>
            {/* Big total number */}
            <Typography
              sx={{
                fontSize: "42px",
                fontWeight: 700,
                color: "#1c2b4a",
                lineHeight: 1,
                fontFamily: "'Outfit', sans-serif",
                mb: 1.5,
              }}
            >
              {leftValue}
            </Typography>

            {/* Passed row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: isPassed ? "#10b981" : "#dde4ee",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "12.5px", color: "#6b7a90" }}>
                Passed:{" "}
                <Box component="span" sx={{ fontWeight: 700, color: isPassed ? "#10b981" : "#cdd6e4" }}>
                  {passed}
                </Box>
              </Typography>
            </Box>

            {/* Failed row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: failed > 0 ? "#e0e7ef" : "#e0e7ef",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "12.5px", color: "#6b7a90" }}>
                Failed:{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#9ab0c8" }}>
                  {failed}
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TwoSectionCard;